import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useContracts, useClients, useConvenios } from '../../contexts/DataContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { Input } from '../../components/input';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import { FianzaView } from '../../components/FianzaView';
import TablePager from '../../components/TablePager';
import { getPrimaAmount, getEmisorNombre, getTipoFianza, getBeneficiario, getFechaEmision, formatNumeroFianza } from '../../utils/calculations';
import { getFianzaStatus, normalizeName } from '../../utils/convenioCompliance';
import { getEstadoFianza, ESTADO_FIANZA } from '../../utils/fianzaEventos';
import { SERIES_COLORS, OTROS_COLOR } from '../../utils/chartPalette';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

const titleCase = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

// Fianzas whose XML carried no beneficiario still are production: they get
// their own bucket instead of being dropped, so the totals below match the
// year's emisiones.
const SIN_BENEFICIARIO = '__sin_beneficiario__';

const STATUS_BADGE = {
  vigente: 'bg-green-100 text-green-800',
  vencida: 'bg-red-100 text-red-800',
  pendiente: 'bg-gray-100 text-gray-800',
};

export function Beneficiarios({ selectedYear }) {
  const { contractsList } = useContracts();
  const { clientsById } = useClients();
  const { conveniosById } = useConvenios();

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [selectedFianza, setSelectedFianza] = useState(null);
  const perPage = 10;

  const rows = useMemo(() => {
    const map = new Map();
    contractsList.forEach((contract) => {
      const clienteNombre = contract.convenioId
        ? conveniosById?.[contract.convenioId]?.nombre || 'Convenio'
        : clientsById[contract.clientId]?.nombreEmpresa || 'Sin nombre';

      (contract.fianzas || []).forEach((fianza) => {
        // Reportes rule: anuladas/sustituidas out, canceladas retained.
        const estado = getEstadoFianza(fianza);
        if (estado === ESTADO_FIANZA.ANULADA || estado === ESTADO_FIANZA.SUSTITUIDA) return;
        const emisionMs = getFechaEmision(fianza);
        if (!emisionMs || new Date(emisionMs).getFullYear() !== selectedYear) return;
        // The beneficiario name comes off the afianzadora's XML, and the same
        // company arrives spelled differently from different carriers
        // ("BEIERSDORF ... MEXICO S.A. DE C.V." vs "..., S.A. DE C.V."). Group
        // on the normalized name so one beneficiario is one row; the label is
        // resolved afterwards from the variant seen most often.
        const nombre = (getBeneficiario(fianza).nombre || '').trim();
        const normalized = normalizeName(nombre);
        const key = normalized ? `name:${normalized}` : SIN_BENEFICIARIO;
        const entry = map.get(key) || {
          key,
          nombre: nombre || 'Sin beneficiario',
          variantes: new Map(),
          count: 0,
          vigentes: 0,
          monto: 0,
          prima: 0,
          clientes: new Set(),
          afianzadoras: new Set(),
          fianzas: [],
        };
        const status = getFianzaStatus(fianza);
        if (nombre) entry.variantes.set(nombre, (entry.variantes.get(nombre) || 0) + 1);
        entry.count += 1;
        if (status.code === 'vigente') entry.vigentes += 1;
        entry.monto += Number(fianza.montoAfianzado) || 0;
        entry.prima += getPrimaAmount(fianza) || 0;
        entry.clientes.add(clienteNombre);
        entry.afianzadoras.add(getEmisorNombre(fianza) || 'Sin afianzadora');
        entry.fianzas.push({
          fianza: { ...fianza, contratoId: contract.id, clientId: contract.clientId || null },
          clienteNombre,
          tipo: titleCase(getTipoFianza(fianza) || 'Sin tipo'),
          statusCode: status.code,
          monto: Number(fianza.montoAfianzado) || 0,
        });
        map.set(key, entry);
      });
    });
    // Label = the most frequent spelling of the name; ties go to the longest,
    // which is the one that kept its legal suffix.
    map.forEach((entry) => {
      let best = null;
      entry.variantes.forEach((count, variante) => {
        if (!best || count > best.count || (count === best.count && variante.length > best.variante.length)) {
          best = { variante, count };
        }
      });
      if (best) entry.nombre = best.variante;
      delete entry.variantes;
    });
    // "Sin beneficiario" sorts last: it is a data gap, not the biggest client.
    return Array.from(map.values()).sort((a, b) => {
      if (a.key === SIN_BENEFICIARIO) return 1;
      if (b.key === SIN_BENEFICIARIO) return -1;
      return b.monto - a.monto;
    });
  }, [contractsList, clientsById, conveniosById, selectedYear]);

  const filtered = useMemo(() => {
    if (!searchTerm) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter(
      (r) =>
        r.nombre.toLowerCase().includes(q) ||
        Array.from(r.clientes).some((c) => c.toLowerCase().includes(q))
    );
  }, [rows, searchTerm]);

  const totals = useMemo(() => ({
    beneficiarios: rows.length,
    monto: rows.reduce((s, r) => s + r.monto, 0),
    fianzas: rows.reduce((s, r) => s + r.count, 0),
  }), [rows]);

  // Donut over the UNFILTERED ranking: top 7 + "Otros". Colors follow the
  // monto ranking (stable — rows never re-sort), gray for the fold.
  const { donutData, colorByKey } = useMemo(() => {
    const map = new Map();
    rows.slice(0, SERIES_COLORS.length - 1).forEach((r, i) => map.set(r.key, SERIES_COLORS[i]));
    const head = rows.slice(0, SERIES_COLORS.length - 1).map((r) => ({
      key: r.key,
      nombre: titleCase(r.nombre),
      rawNombre: r.nombre,
      monto: r.monto,
    }));
    const tail = rows.slice(SERIES_COLORS.length - 1);
    if (tail.length === 1) {
      map.set(tail[0].key, SERIES_COLORS[SERIES_COLORS.length - 1]);
      head.push({ key: tail[0].key, nombre: titleCase(tail[0].nombre), rawNombre: tail[0].nombre, monto: tail[0].monto });
    } else if (tail.length > 1) {
      head.push({
        key: '__otros__',
        nombre: `Otros (${tail.length})`,
        rawNombre: null,
        monto: tail.reduce((s, r) => s + r.monto, 0),
      });
    }
    return { donutData: head, colorByKey: map };
  }, [rows]);

  const colorFor = (key) =>
    key === '__otros__' ? OTROS_COLOR : colorByKey.get(key) || OTROS_COLOR;
  const donutMax = donutData.reduce((m, d) => Math.max(m, d.monto), 0);

  const pagedRows = filtered.slice((page - 1) * perPage, page * perPage);

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div>
      <div className="mb-4">
        <Heading level={2} className="text-xl text-azul-surelia">Beneficiarios</Heading>
        <Text className="text-sm text-azul-surelia/70">
          A quién garantizan las fianzas emitidas en {selectedYear}
        </Text>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="rounded-lg border border-azul-surelia/20 bg-white p-3 min-w-[140px]">
          <Text className="text-xs text-azul-surelia/70">Beneficiarios</Text>
          <Text className="text-xl font-bold text-azul-surelia tabular-nums">{totals.beneficiarios}</Text>
        </div>
        <div className="rounded-lg border border-azul-surelia/20 bg-white p-3 min-w-[180px]">
          <Text className="text-xs text-azul-surelia/70">Monto afianzado</Text>
          <Text className="text-xl font-bold text-azul-surelia tabular-nums">{fmtMXN(totals.monto)}</Text>
        </div>
        <div className="rounded-lg border border-azul-surelia/20 bg-white p-3 min-w-[140px]">
          <Text className="text-xs text-azul-surelia/70">Fianzas</Text>
          <Text className="text-xl font-bold text-azul-surelia tabular-nums">{totals.fianzas}</Text>
        </div>
        <Input
          type="text"
          placeholder="Buscar beneficiario o cliente..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          className="flex-1 min-w-[220px] sm:max-w-xs ml-auto"
        />
      </div>

      {/* Distribución del monto por beneficiario */}
      {rows.length > 0 && (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 items-center">
            <div>
              <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="monto"
                    nameKey="nombre"
                    innerRadius="62%"
                    outerRadius="95%"
                    paddingAngle={1}
                    stroke="#ffffff"
                    strokeWidth={2}
                    onClick={(entry) => {
                      if (entry.rawNombre) {
                        setSearchTerm(searchTerm === entry.rawNombre ? '' : entry.rawNombre);
                        setPage(1);
                      }
                    }}
                  >
                    {donutData.map((d) => (
                      <Cell key={d.key} fill={colorFor(d.key)} cursor={d.rawNombre ? 'pointer' : 'default'} />
                    ))}
                  </Pie>
                  <ChartTooltip formatter={(value, name) => [fmtMXN(value), name]} />
                </PieChart>
              </ResponsiveContainer>
              </div>
              <div className="text-center mt-1">
                <div className="text-base font-bold text-azul-surelia tabular-nums">{fmtMXN(totals.monto)}</div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">Monto afianzado</div>
              </div>
            </div>

            <div>
              {donutData.map((d) => {
                const share = totals.monto > 0 ? Math.round((d.monto / totals.monto) * 100) : 0;
                const width = donutMax > 0 ? Math.max(2, (d.monto / donutMax) * 100) : 0;
                const color = colorFor(d.key);
                const active = d.rawNombre && searchTerm === d.rawNombre;
                return (
                  <div
                    key={d.key}
                    className={`rounded-lg -mx-2 px-2 py-1.5 transition-colors ${
                      d.rawNombre ? 'cursor-pointer hover:bg-azul-surelia/5' : ''
                    } ${active ? 'bg-azul-surelia/5' : ''}`}
                    onClick={() => {
                      if (d.rawNombre) {
                        setSearchTerm(active ? '' : d.rawNombre);
                        setPage(1);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }}></span>
                        <span className="text-sm font-medium text-zinc-900 truncate" title={d.nombre}>{d.nombre}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-sm font-semibold text-azul-surelia tabular-nums">{fmtMXN(d.monto)}</span>
                        <span className="text-[11px] text-gray-400 ml-2">{share}%</span>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: color }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <Text className="text-sm text-zinc-500 py-8 text-center">
          {rows.length === 0 ? `Sin fianzas emitidas en ${selectedYear}` : 'Sin resultados'}
        </Text>
      ) : (
        <>
          <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg">
            <Table>
              <TableHead className="bg-azul-surelia text-white">
                <TableRow>
                  <TableHeader className="text-white w-8"></TableHeader>
                  <TableHeader className="text-white">Beneficiario</TableHeader>
                  <TableHeader className="text-white text-right">Fianzas</TableHeader>
                  <TableHeader className="text-white text-right">Monto afianzado</TableHeader>
                  <TableHeader className="text-white text-right">Prima</TableHeader>
                  <TableHeader className="text-white">Clientes</TableHeader>
                  <TableHeader className="text-white text-right">Afianzadoras</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r) => {
                  const expanded = expandedKeys.has(r.key);
                  const clientesArr = Array.from(r.clientes);
                  return (
                    <React.Fragment key={r.key}>
                      <TableRow
                        className="cursor-pointer hover:bg-azul-surelia/5"
                        onClick={() => toggleExpanded(r.key)}
                      >
                        <TableCell className="py-3 pr-0">
                          {expanded ? (
                            <ChevronDownIcon className="w-4 h-4 text-zinc-400" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4 text-zinc-400" />
                          )}
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: colorFor(r.key) }}
                            ></span>
                            <Text className="text-sm font-medium text-azul-surelia" title={r.nombre}>
                              {titleCase(r.nombre)}
                            </Text>
                          </div>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text className="text-sm text-zinc-700 tabular-nums">
                            {r.count}
                            {r.vigentes !== r.count && (
                              <span className="text-zinc-400"> ({r.vigentes} vig.)</span>
                            )}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text className="text-sm font-semibold text-azul-surelia tabular-nums">{fmtMXN(r.monto)}</Text>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text className="text-sm text-zinc-700 tabular-nums">{fmtMXN(r.prima)}</Text>
                        </TableCell>
                        <TableCell className="py-3">
                          <Text className="text-sm text-zinc-700 truncate max-w-[220px]" title={clientesArr.join(', ')}>
                            {clientesArr.length === 1
                              ? clientesArr[0]
                              : `${clientesArr[0]} y ${clientesArr.length - 1} más`}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text className="text-sm text-zinc-700 tabular-nums">{r.afianzadoras.size}</Text>
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow className="bg-azul-surelia/5">
                          <TableCell colSpan={7} className="py-2 px-6">
                            <div className="sticky left-0 max-w-4xl space-y-1">
                              {r.fianzas.map((f) => (
                                <button
                                  key={f.fianza.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedFianza(f.fianza);
                                  }}
                                  className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left bg-white rounded-lg border border-zinc-200 px-3 py-1.5 hover:border-azul-surelia/40 transition-colors"
                                >
                                  <span className="text-sm font-mono text-zinc-700 min-w-[130px]">
                                    {formatNumeroFianza(f.fianza, 'Sin número')}
                                  </span>
                                  <span className="text-xs text-zinc-500 min-w-[140px] truncate">{f.clienteNombre}</span>
                                  <span className="text-xs text-zinc-500 truncate">{f.tipo}</span>
                                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_BADGE[f.statusCode] || 'bg-gray-100 text-gray-800'}`}>
                                    {f.statusCode}
                                  </span>
                                  <span className="text-xs text-zinc-600 tabular-nums">{fmtMXN(f.monto)}</span>
                                </button>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <TablePager total={filtered.length} page={page} onPage={setPage} perPage={perPage} itemLabel="beneficiarios" />
        </>
      )}

      {selectedFianza &&
        createPortal(
          <FianzaView
            isOpen={!!selectedFianza}
            onClose={() => setSelectedFianza(null)}
            fianza={selectedFianza}
            clientData={clientsById[selectedFianza.clientId] || {}}
            contratoData={contractsList.find((c) => c.id === selectedFianza.contratoId)}
            onUpdate={() => {}}
          />,
          document.body
        )}
    </div>
  );
}
